import { Suspense, lazy } from 'react';
import { LoadingSpinner } from './LoadingSpinner';

/**
 * HOC for lazy loading components with loading state
 * Usage: const LazyComponent = lazyLoad(() => import('./MyComponent'));
 */
export function lazyLoad(importFunc, fallback = null) {
  const LazyComponent = lazy(importFunc);
  
  return function LazyLoadedComponent(props) {
    return (
      <Suspense fallback={fallback || <LoadingSpinner fullPage={false} size="md" />}>
        <LazyComponent {...props} />
      </Suspense>
    );
  };
}

/**
 * Pre-built lazy loaded components wrapper
 */
export function LazyLoadWrapper({ children, fallback }) {
  return (
    <Suspense fallback={fallback || <LoadingSpinner fullPage={false} size="md" />}>
      {children}
    </Suspense>
  );
}

/**
 * Page-level lazy loading with full-page spinner
 */
export function LazyPage({ component: Component, ...props }) {
  return (
    <Suspense fallback={<LoadingSpinner fullPage text="Loading page..." />}>
      <Component {...props} />
    </Suspense>
  );
}

/**
 * Section-level lazy loading (for below-the-fold content)
 */
export function LazySection({ children, minHeight = '200px', fallback }) {
  return (
    <Suspense 
      fallback={
        fallback || (
          <div 
            style={{ minHeight }} 
            className="flex items-center justify-center bg-gray-50 rounded-lg"
          >
            <LoadingSpinner fullPage={false} size="sm" showText={false} />
          </div>
        )
      }
    >
      {children}
    </Suspense>
  );
}

/**
 * Error boundary for lazy loaded components
 */
export class LazyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Lazy loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <p className="text-red-600 font-medium mb-2">Failed to load component</p>
          <button 
            onClick={() => window.location.reload()}
            className="text-sm text-[#FF6B35] hover:underline"
          >
            Reload page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Need to import React for class component
import React from 'react';
